import { getProductCatalogNumber } from './productDisplay';

/** Preview uploads stay at the same per-file cap as other PDFs in this app. */
export const RECEIPT_PREVIEW_MAX_BYTES = 10 * 1024 * 1024;

const RECEIPT_PREVIEW_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.gif'];

const RECEIPT_PREVIEW_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

/**
 * Why a chosen scan cannot be sent to the order-free receipt preview.
 * An empty string means the file is acceptable.
 *
 * @param {File|null|undefined} file
 * @returns {string}
 */
export function receiptPreviewFileError(file) {
  if (!file) return 'Choose a scanned receipt.';
  const name = String(file.name || '').toLowerCase();
  const extensionOk = RECEIPT_PREVIEW_EXTENSIONS.some(extension =>
    name.endsWith(extension)
  );
  const type = String(file.type || '');
  const typeOk =
    !type ||
    type === 'application/octet-stream' ||
    RECEIPT_PREVIEW_MIME_TYPES.has(type);
  if (!extensionOk || !typeOk) {
    return 'Choose a PDF or an image (PNG, JPEG, WEBP, or GIF).';
  }
  if (file.size > RECEIPT_PREVIEW_MAX_BYTES) {
    return 'File size must be less than 10MB.';
  }
  return '';
}

/**
 * Order image ids whose S3 upload status is `completed`.
 * Failed uploads are left out so they are not sent to receipt-reads.
 *
 * @param {Object<string, string>|null|undefined} uploadStatuses
 * @returns {number[]}
 */
export function completedReceiptImageIds(uploadStatuses) {
  if (!uploadStatuses || typeof uploadStatuses !== 'object') return [];
  return Object.entries(uploadStatuses)
    .filter(([, status]) => status === 'completed')
    .map(([id]) => Number(id))
    .filter(id => Number.isFinite(id));
}

/**
 * Catalogue number and name for a matched order line, when the id is on the order.
 *
 * @param {Array<{ id?: number|string, product?: Object, product_name?: string }>|null|undefined} orderItems
 * @param {number|string|null|undefined} orderItemId
 * @returns {string}
 */
export function orderLineLabelForReceipt(orderItems, orderItemId) {
  if (orderItemId == null || orderItemId === '') return '';
  const item = (orderItems || []).find(
    row => String(row?.id) === String(orderItemId)
  );
  if (!item) return '';
  const name = item.product?.name || item.product_name || '';
  const cat = getProductCatalogNumber(item.product) || '';
  if (cat && name) return `${cat} — ${name}`;
  return name || cat;
}

/**
 * User-facing text for a receipt-read error body.
 *
 * @param {Object|Array|string|null|undefined} errPayload
 * @returns {string}
 */
export function formatReceiptReadError(errPayload) {
  if (errPayload == null || errPayload === '') {
    return 'Could not read this receipt.';
  }
  if (typeof errPayload === 'string') return errPayload;
  if (Array.isArray(errPayload)) {
    const joined = errPayload.map(item => String(item)).filter(Boolean).join(' ');
    return joined || 'Could not read this receipt.';
  }
  if (typeof errPayload.detail === 'string' && errPayload.detail) {
    return errPayload.detail;
  }
  const parts = [];
  Object.values(errPayload).forEach(value => {
    if (Array.isArray(value)) {
      value.forEach(item => parts.push(String(item)));
    } else if (typeof value === 'string' && value) {
      parts.push(value);
    }
  });
  return parts.join(' ') || 'Could not read this receipt.';
}
